'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User } from '@supabase/supabase-js';

export type UserRole = 'admin' | 'user';

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  mustResetPassword: boolean;
  createdAt: string;
  lastLogin?: string;
  profilePhoto?: string;
}

interface AuthContextType {
  currentUser: AppUser | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; mustResetPassword?: boolean }>;
  logout: () => void;
  resetPassword: (userId: string, newPassword: string) => Promise<boolean>;
  changeOwnPassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (updates: Partial<Pick<AppUser, 'name' | 'profilePhoto'>>) => Promise<{ success: boolean; error?: string }>;
  // Admin user management
  getAllUsers: () => Promise<AppUser[]>;
  createUser: (email: string, name: string, role: UserRole) => Promise<{ success: boolean; tempPassword?: string; error?: string }>;
  updateUser: (userId: string, updates: { name?: string; role?: UserRole }) => Promise<{ success: boolean; error?: string }>;
  toggleUserActive: (userId: string) => Promise<{ success: boolean; error?: string }>;
  adminResetPassword: (userId: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function mapProfile(row: any): AppUser {
  return {
    id: row.id,
    email: row.email,
    name: row.name || '',
    role: row.role as UserRole,
    isActive: row.is_active,
    mustResetPassword: row.must_reset_password,
    createdAt: row.created_at,
    lastLogin: row.last_login ?? undefined,
    profilePhoto: row.profile_photo ?? undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        await loadProfile(session.user);
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await loadProfile(session.user);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(user: User) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error || !data) {
        // No profile row yet — this happens for users added directly in Supabase Dashboard.
        // Create the profile row now so they can log in and change their password.
        const meta = user.user_metadata || {};
        const newProfile = {
          id: user.id,
          email: user.email || '',
          name: meta.name || user.email?.split('@')[0] || '',
          role: (meta.role as UserRole) || 'user',
          is_active: true,
          must_reset_password: true, // Always require password change for admin-created users
          created_at: user.created_at,
        };

        const { data: created } = await supabase
          .from('user_profiles')
          .upsert(newProfile, { onConflict: 'id' })
          .select()
          .single();

        setCurrentUser(
          created
            ? mapProfile(created)
            : {
                id: user.id,
                email: user.email || '',
                name: newProfile.name,
                role: newProfile.role,
                isActive: true,
                mustResetPassword: true,
                createdAt: user.created_at,
              }
        );
        return;
      }

      // Always use the role from the database — this is the authoritative source
      setCurrentUser(mapProfile(data));
    } catch {
      setCurrentUser(null);
    }
  }

  async function login(email: string, password: string): Promise<{ success: boolean; error?: string; mustResetPassword?: boolean }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message.toLowerCase().includes('invalid login credentials') ||
            error.message.toLowerCase().includes('invalid credentials')) {
          return { success: false, error: 'Incorrect email or password. Please try again.' };
        }
        return { success: false, error: error.message };
      }
      if (!data.user) return { success: false, error: 'Login failed. Please try again.' };

      // Check if user is active and get must_reset_password flag
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_active, must_reset_password')
        .eq('id', data.user.id)
        .single();

      if (profile && !profile.is_active) {
        await supabase.auth.signOut();
        return { success: false, error: 'This account has been deactivated. Contact your admin.' };
      }

      // Update last_login
      await supabase
        .from('user_profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id);

      return { success: true, mustResetPassword: profile?.must_reset_password ?? false };
    } catch (err: any) {
      return { success: false, error: err.message || 'Login failed.' };
    }
  }

  async function logout() {
    await supabase.auth.signOut();
    setCurrentUser(null);
  }

  async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    if (!currentUser) return { success: false, error: 'Not authenticated.' };
    try {
      // Re-authenticate to verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: currentPassword,
      });
      if (signInError) return { success: false, error: 'Current password is incorrect.' };

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { success: false, error: error.message };

      // Clear must_reset_password flag
      await supabase
        .from('user_profiles')
        .update({ must_reset_password: false })
        .eq('id', currentUser.id);

      setCurrentUser(prev => prev ? { ...prev, mustResetPassword: false } : null);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to change password.' };
    }
  }

  async function updateProfile(updates: Partial<Pick<AppUser, 'name' | 'profilePhoto'>>): Promise<{ success: boolean; error?: string }> {
    if (!currentUser) return { success: false, error: 'Not authenticated.' };
    try {
      const dbUpdates: any = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.profilePhoto !== undefined) dbUpdates.profile_photo = updates.profilePhoto ?? null;

      const { error } = await supabase
        .from('user_profiles')
        .update(dbUpdates)
        .eq('id', currentUser.id);

      if (error) return { success: false, error: error.message };

      setCurrentUser(prev => prev ? { ...prev, ...updates } : null);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update profile.' };
    }
  }

  // Admin: get all users
  async function getAllUsers(): Promise<AppUser[]> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) return [];
      return (data || []).map(mapProfile);
    } catch {
      return [];
    }
  }

  // Admin: create a new user with temp password
  async function createUser(email: string, name: string, role: UserRole): Promise<{ success: boolean; tempPassword?: string; error?: string }> {
    try {
      // Generate temp password
      const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
      const tempPassword = Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');

      // Use admin API via edge function or direct signup
      // We use signUp with auto-confirm (email confirmation disabled in Supabase)
      const { data, error } = await supabase.auth.signUp({
        email,
        password: tempPassword,
        options: {
          data: {
            name,
            role,
            is_active: true,
            must_reset_password: true,
          },
        },
      });

      if (error) {
        if (error.message.toLowerCase().includes('already registered') ||
            error.message.toLowerCase().includes('already been registered')) {
          return { success: false, error: 'A user with this email already exists.' };
        }
        return { success: false, error: error.message };
      }

      if (!data.user) return { success: false, error: 'Failed to create user.' };

      // Ensure profile is created/updated with correct data
      await supabase
        .from('user_profiles')
        .upsert({
          id: data.user.id,
          email,
          name,
          role,
          is_active: true,
          must_reset_password: true,
          created_at: new Date().toISOString(),
        }, { onConflict: 'id' });

      return { success: true, tempPassword };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to create user.' };
    }
  }

  // Admin: update user name/role
  async function updateUser(userId: string, updates: { name?: string; role?: UserRole }): Promise<{ success: boolean; error?: string }> {
    try {
      const dbUpdates: any = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.role !== undefined) dbUpdates.role = updates.role;

      const { error } = await supabase
        .from('user_profiles')
        .update(dbUpdates)
        .eq('id', userId);

      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update user.' };
    }
  }

  // Admin: toggle user active status
  async function toggleUserActive(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: profile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('is_active')
        .eq('id', userId)
        .single();

      if (fetchError || !profile) return { success: false, error: 'User not found.' };

      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: !profile.is_active })
        .eq('id', userId);

      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update user status.' };
    }
  }

  // Admin: reset another user's password
  async function adminResetPassword(userId: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Mark must_reset_password in profile
      const { error } = await supabase
        .from('user_profiles')
        .update({ must_reset_password: true })
        .eq('id', userId);

      if (error) return { success: false, error: error.message };

      // Note: Direct password reset for other users requires Supabase Admin API (service role key)
      // which should not be exposed client-side. We store the new password intent via must_reset_password.
      // For a full implementation, use a Supabase Edge Function with service role key.
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to reset password.' };
    }
  }

  // Legacy compatibility: direct password reset (admin sets password for user)
  async function resetPassword(userId: string, newPassword: string): Promise<boolean> {
    const result = await adminResetPassword(userId, newPassword);
    return result.success;
  }

  return (
    <AuthContext.Provider value={{
      currentUser,
      isAdmin: currentUser?.role === 'admin',
      isAuthenticated: !!currentUser,
      loading,
      login,
      logout,
      resetPassword,
      changeOwnPassword,
      updateProfile,
      getAllUsers,
      createUser,
      updateUser,
      toggleUserActive,
      adminResetPassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Legacy exports for backward compatibility with admin/users page
export function getAllUsers(): AppUser[] { return []; }
export function saveUsers(_users: AppUser[]): void {}
export function savePasswords(_passwords: Record<string, string>): void {}
export function getPasswords(): Record<string, string> { return {}; }
